import { useCallback, useEffect, useRef, useState } from "react";
import { useSocketContext } from "../context/SocketProvider";

export interface SocketMessage {
  _id: string;
  senderId: string;
  receiverId?: string;
  direction: "sent" | "received";
  text: string;
  timestamp: Date;
  status: "sent" | "delivered" | "read";
  receipts: Array<{
    fiUser: string;
    status: "sent" | "delivered" | "seen";
    updatedOn: Date;
  }>;
  senderName?: string;
  replyTo?: {
    id: string;
    text: string;
    senderName: string;
  };
  reactions: Array<{
    userId: string;
    reaction: string;
  }>;
  createdOn: Date;
  chatId: string;
  deliveryStatus: "sent" | "delivered" | "read";
  referenceType?: "process" | "screen" | "component";
  isMessageRead: boolean;
  reference?: {
    screenId?: string;
    screenLabel?: string;
    componentId?: string;
    componentLabel?: string;
  };
}

export interface PresenceUser {
  userId: string;
  status: "online" | "offline";
}

export interface SocketState {
  presenceUsers: Map<string, PresenceUser>; // userId -> presence info
}

export interface UseSocketReturn extends SocketState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  joinChat: (chatId: string) => void;
  leaveChat: (chatId: string) => void;
  sendMessage: (params: {
    chatId: string;
    content: string;
    replyTo?: string | null;
  }) => Promise<SocketMessage>;
  sendTypingStart: (chatId: string) => void;
  sendTypingStop: (chatId: string) => void;
  debouncedTypingStop: (chatId: string) => void;
  markMessageAsRead: (messageId: string, chatId: string) => void;
  isUserOnline: (userId: string) => boolean;
  getUserLastSeen: (userId: string) => string | null;
  reconnect: () => void;
}

export const useSocket = () => {
  const { socket, isConnected, isConnecting, error, reconnect } =
    useSocketContext();

  const [socketState, setSocketState] = useState<SocketState>({
    presenceUsers: new Map(),
  });

  const currentChatIdRef = useRef<string | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Setup socket event listeners
  useEffect(() => {
    if (!socket) return;

    const presenceHandler = (data: {
      userId: string;
      status: "online" | "offline";
    }) => {
        setSocketState((prev) => {
          console.log("Hello online", data);
          const newPresenceUsers = new Map(prev.presenceUsers);

          // Only update if we already have this user in our presence map
          // (i.e., we're subscribed to them or got their initial status)
          if (newPresenceUsers.has(data.userId)) {
            const existingUser = newPresenceUsers.get(data.userId);
            newPresenceUsers.set(data.userId, {
              ...existingUser!,
              status: data.status,
            });
          } else {
            // If we don't have initial data, create new entry
            newPresenceUsers.set(data.userId, {
              userId: data.userId,
              status: data.status,
            });
          }

          return {
            ...prev,
            presenceUsers: newPresenceUsers,
          };
        });
    };

    socket.on("presence:update", presenceHandler);

    return () => {
      // Only remove the handler this hook registered. Calling socket.off(event)
      // without a handler removes ALL listeners for that event on the shared
      // socket, which silently killed other components' chat listeners.
      socket.off("presence:update", presenceHandler);
    };
  }, [socket]);

  // Join chat room
  const joinChat = useCallback(
    (chatId: string) => {
      if (!socket?.connected) return;

      currentChatIdRef.current = chatId;
      socket.emit("join_chat", { chatId });
    },
    [socket]
  );

  // Leave chat room
  const leaveChat = useCallback(
    (chatId: string) => {
      if (!socket?.connected) return;

      if (currentChatIdRef.current === chatId) {
        currentChatIdRef.current = null;
      }

      socket.emit("leave_chat", { chatId });
    },
    [socket]
  );

  const subscribeForUserOnlineStatus = useCallback(
    (userIds: string) => {
      if (!socket?.connected) return;

      socket.emit("presence:subscribe", { userIds });
    },
    [socket]
  );

  // Send typing start
  const sendTypingStart = useCallback(
    (chatId: string) => {
      if (!socket?.connected) return;

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      socket.emit("typing", { chatId, isTyping: true });
    },
    [socket]
  );

  // Send typing stop
  const sendTypingStop = useCallback(
    (chatId: string) => {
      if (!socket?.connected) return;

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      socket.emit("typing", { chatId, isTyping: false });
    },
    [socket]
  );

  // Debounced typing stop - automatically stop typing after 2 seconds of inactivity
  const debouncedTypingStop = useCallback(
    (chatId: string) => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        sendTypingStop(chatId);
      }, 2000); // Stop typing indicator after 2 seconds of inactivity
    },
    [sendTypingStop]
  );

  // Send message with Socket.IO ack
  const sendMessage = useCallback(
    (params: {
      chatId: string;
      content: string;
      replyTo?: string | null;
      referenceType?: "screen" | "component";
      reference?: {
        screen?: string;
        screenLabel?: string;
        component?: string;
      };
    }) => {
      if (!socket?.connected) {
        return Promise.reject(new Error("Socket not connected"));
      }

      if (!params.content?.trim()) {
        return Promise.reject(new Error("Message is empty"));
      }

      // Ensure typing indicator stops when sending
      if (currentChatIdRef.current) {
        sendTypingStop(currentChatIdRef.current);
      }

      return new Promise<SocketMessage>((resolve, reject) => {
        // Set a timeout for the acknowledgment
        const timeout = setTimeout(() => {
          reject(new Error("Socket timeout - no acknowledgment received"));
        }, 10000); // 10 second timeout

        socket.emit(
          "send_message",
          {
            chatId: params.chatId,
            content: params.content,
            replyTo: params.replyTo ?? null,
            referenceType: params.referenceType,
            reference: params.reference,
          },
          (ack: {
            ok: boolean;
            message?: SocketMessage;
            error?: string | { message: string };
          }) => {
            clearTimeout(timeout); // Clear timeout since we got a response
            if (ack && ack.ok && ack.message) {
              resolve(ack.message);
            } else {
              // Handle both string and object error formats
              let errorMsg = "Failed to send message";
              if (ack?.error) {
                if (typeof ack.error === "string") {
                  errorMsg = ack.error;
                } else if (
                  typeof ack.error === "object" &&
                  ack.error !== null &&
                  "message" in ack.error
                ) {
                  errorMsg = ack.error.message;
                } else {
                  errorMsg = JSON.stringify(ack.error);
                }
              }
              reject(new Error(errorMsg));
            }
          }
        );
      });
    },
    [socket, sendTypingStop]
  );

  // Mark message as read
  const markMessageAsRead = useCallback(
    (messageId: string, chatId: string) => {
      if (!socket?.connected) return;

      socket.emit("mark_message_read", { messageId, chatId });
    },
    [socket]
  );

  return {
    ...socketState,
    isConnected,
    isConnecting,
    error,
    joinChat,
    leaveChat,
    sendMessage,
    sendTypingStart,
    sendTypingStop,
    debouncedTypingStop,
    markMessageAsRead,
    // isUserOnline,
    // getUserLastSeen,
    reconnect,
    subscribeForUserOnlineStatus,
  };
};
